import { store } from '../stores/store';
import { ApplicationType } from '../typegen/application-type';
import { Log } from '../typegen/log';
import { LogBarcodeScanner } from '../typegen/log-barcode-scanner';
import { LogCardPayment } from '../typegen/log-card-payment';
import { LogCardReader } from '../typegen/log-card-reader';
import { LogCashAcceptor } from '../typegen/log-cash-acceptor';
import { LogCashPayment } from '../typegen/log-cash-payment';
import { LogCheckPayment } from '../typegen/log-check-payment';
import { LogCheckScanner } from '../typegen/log-check-scanner';
import { LogCIS } from '../typegen/log-cis';
import { LogGeneral } from '../typegen/log-general';
import { LogLevelType } from '../typegen/log-level-type';
import { LogNavigation } from '../typegen/log-navigation';
import { LogPP } from '../typegen/log-pp';
import { LogTranslation } from '../typegen/log-translation';
import { LogType } from '../typegen/log-type';
import { ulid } from 'ulid';
import { ILoggerService } from '../types/iLoggerServiceType';
import sqlite from './sqlite';
import KioAppHelper from './kioApp';
import { SupportedDevices } from '../typegen/supported-devices';

type CreateLogDto<T> = Omit<T, 'application' | 'createdAt' | 'id' | 'level' | 'message' | 'type' | 'kioskId' | 'kioskSessionId'>;

function getLogTypeFriendlyName(value: LogType): [string, LogType] | undefined {
	const name = LogType[value];
	return typeof name === 'string' ? [name, value] : undefined;
}

const stringify = (data?: unknown) => (typeof data === 'string' ? data : JSON.stringify(data));

const DEDUP_WINDOW_MS = 1000;
const recentLogs = new Map<string, number>();

function isDuplicate(key: string): boolean {
	const now = Date.now();
	const lastSeen = recentLogs.get(key);
	if (lastSeen !== undefined && now - lastSeen < DEDUP_WINDOW_MS) return true;
	recentLogs.set(key, now);
	if (recentLogs.size > 200) {
		for (const [k, ts] of recentLogs) {
			if (now - ts >= DEDUP_WINDOW_MS) recentLogs.delete(k);
		}
	}
	return false;
}

const debugLog = (type: 'log' | 'warn' | 'error', colorCode: string) => (message: string, data?: unknown) => {
	let showDebugLog = import.meta.env.DEV;
	if (import.meta.env.VITE_LOGGER_DISABLE_DEBUG_LOG === 'true') showDebugLog = false;
	if (KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_SHOW_DEBUG_LOGS) === 'true') showDebugLog = true;
	if (!showDebugLog) return;
	const dedupKey = `debug|${type}|${message}|${stringify(data)}`;
	if (isDuplicate(dedupKey)) return;
	console[type](`\x1b[${colorCode}m[DEBUG]\x1b[0m`, `message: ${message}`, 'data:', stringify(data));
};

type LogHandler<T extends Log> = {
	log: (log: CreateLogDto<T>, message: string, data?: unknown, skipDedup?: boolean) => Promise<void>;
	warn: (log: CreateLogDto<T>, message: string, data?: unknown, skipDedup?: boolean) => Promise<void>;
	error: (log: CreateLogDto<T>, message: string, exception: unknown, data?: unknown, skipDedup?: boolean) => Promise<void>;
};

/**
 * Creates a log handler for the specified log type.
 *
 * @template T - The type of log data.
 * @param {LogType} logType - The type of log.
 * @returns {LogHandler<T>} A log handler for the specified log type.
 */
const logHandler = <T extends Log>(logType: LogType): LogHandler<T> => {
	/**
	 * Writes a log entry.
	 *
	 * @param {LogLevelType} level - The log level.
	 * @param {CreateLogDto<T>} log - The log data. Sometimes you will need to have an emtpty object '{}'
	 * @param {string} message - The log message.
	 * @param {unknown} [exception] - The exception message (optional).
	 * @param {unknown} [data] - Additional log data (optional).
	 * @param {boolean} [skipDedup=false] - If true, bypasses deduplication and always writes the log entry.
	 */
	const writeLog = async (
		level: LogLevelType,
		log: CreateLogDto<T>,
		message: string,
		exception?: unknown,
		data?: unknown,
		skipDedup = false
	): Promise<void> => {
		if (!isLogRequired(logType)) {
			debugLog('log', '36')(`Skipping log of type ${LogType[logType]} as it is not required.`);
			return;
		}

		if (!skipDedup) {
			const dedupKey = `${logType}|${level}|${message}|${stringify(data)}|${stringify(exception)}`;
			if (isDuplicate(dedupKey)) return;
		}

		const fullLog: Log = structuredClone({
			...log,
			id: ulid().toUpperCase(),
			message: message,
			data: stringify(data),
			exception: stringify(exception),
			level: level,
			type: logType,
			application: ApplicationType.Client,
			createdAt: new Date(),
			kioskId: store.kioskStore.kioskId,
			kioskSessionId: store.sessionStore.sessionId,
		});

		if (store.configStore.isConfigLoaded) await sqlite.createLog(fullLog);

		const logName = `${getLogTypeFriendlyName(fullLog.type)}:`;
		let disableLog = !import.meta.env.DEV;

		try {
			const disabledTypes = parseInt(import.meta.env.VITE_LOGGER_DISABLE_LOG_TYPE, 10);
			disableLog = disabledTypes === 0 || (disabledTypes & logType) !== 0;
		} catch (error) {
			logger.debug.error('Error disabling log type:', error);
		}

		if (!disableLog)
			switch (level) {
				case LogLevelType.Error:
					console.error(logName, fullLog);
					break;
				case LogLevelType.Warning:
					console.warn(logName, fullLog);
					break;
				case LogLevelType.Information:
				default:
					console.log(logName, fullLog);
			}
	};

	return {
		log: async (log, message, data, skipDedup) => {
			await writeLog(LogLevelType.Information, log, message, undefined, data, skipDedup);
		},
		warn: async (log, message, data, skipDedup) => {
			await writeLog(LogLevelType.Warning, log, message, undefined, data, skipDedup);
		},
		error: async (log, message, exception, data, skipDedup) => {
			await writeLog(LogLevelType.Error, log, message, exception, data, skipDedup);
		},
	};
};

const isLogRequired = (logType: LogType): boolean => {
	const { isDeviceSupported } = store.kioskStore;

	switch (logType) {
		case LogType.CashAcceptor:
		case LogType.CashPayment:
			return isDeviceSupported(SupportedDevices.CashAcceptor);
		case LogType.CheckScanner:
		case LogType.CheckPayment:
			return isDeviceSupported(SupportedDevices.CheckReader);
		default:
			return true;
	}
};

class Logger implements ILoggerService {
	logCardPayment = logHandler<LogCardPayment>(LogType.CardPayment);
	logNavigation = logHandler<LogNavigation>(LogType.Navigation);
	logGeneral = logHandler<LogGeneral>(LogType.All);
	logBarcodeScanner = logHandler<LogBarcodeScanner>(LogType.BarCodeScanner);
	logCardReader = logHandler<LogCardReader>(LogType.CardReader);
	logCashAcceptor = logHandler<LogCashAcceptor>(LogType.CashAcceptor);
	logCashPayment = logHandler<LogCashPayment>(LogType.CashPayment);
	logCheckPayment = logHandler<LogCheckPayment>(LogType.CheckPayment);
	logCheckScanner = logHandler<LogCheckScanner>(LogType.CheckScanner);
	logCIS = logHandler<LogCIS>(LogType.CustomerInformationSystem);
	logPP = logHandler<LogPP>(LogType.PaymentProvider);
	logTranslation = logHandler<LogTranslation>(LogType.Translation);
	/**
	 * Logs messages to the console only
	 */
	debug = {
		log: debugLog('log', '36'),
		warn: debugLog('warn', '33'),
		error: debugLog('error', '31'),
	};
}

const logger = new Logger();
export default logger;
